import React, { ChangeEvent, FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import { Organization } from 'models/organization';
import { LabWithRanking, Sample } from 'models/order';
import { actionConstructionToRestorationInput } from 'components/order-flow/shared/restoration-input-helper';
import DeliveryDatePicker from 'components/shared/delivery-date-picker/delivery-date-picker.component';
import DeliveryTimeWarning from 'components/shared/delivery-time-warning/delivery-time-warning.component';
import DeliveryClinicPicker from 'components/shared/delivery-clinic-picker/delivery-clinic-picker.component';
import SampleMethod from 'components/lab-order/sample-method/sample-method.component';
import LabTable from './lab-table/lab-delivery-step.component';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { getLabsWithRankingsTemplate, saveOrderDraftTemplate } from 'apollo/templates/order';
import { actionOrderDeliveryDraft, isValidDeliveryDate } from 'components/order-flow/shared/order-input-helper';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ActionConstruction, ActionOrderInput } from 'models/order-input/action-order';
import Loader from 'components/shared/loader/loader.component';
import LabExceptions from './lab-exceptions/lab-exceptions.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { LoadingButton } from '@mui/lab';
import { getRecentlyUsedLabsWithRankingsTemplate } from 'apollo/templates/order/query.graphql';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import { isRealCurrency } from 'utils/currency';
import { RequiredAsterisk } from 'components/ui/required-asteriks';
import { OrderBy, OrderDirection } from 'models/search-result';
import { OrderInputType } from 'models/order-input/shared';

type LabDeliveryStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
};
const LabDeliveryStep: FC<LabDeliveryStepProps> = ({ onBackClick, onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, setOrder } = useContext(ActionOrderContext);
    const [isValid, setIsValid] = useState(false);
    const [labs, setLabs] = useState<LabWithRanking[]>([]);
    const [originalLabs, setOriginalLabs] = useState<LabWithRanking[]>([]);
    const [originalRecentlyUsedLabs, setOriginalRecentlyUsedLabs] = useState<LabWithRanking[]>([]);
    const [recentlyUsedLabs, setRecentlyUsedLabs] = useState<LabWithRanking[]>([]);
    const [lab, setLab] = useState<LabWithRanking | null>(null);
    const [isHoliday, setIsHoliday] = useState<boolean>(false);
    const [rankOneLab, setRankOneLab] = useState<LabWithRanking>({} as LabWithRanking);

    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();

    const isRecentlyUsedLabsEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings || [],
        CONSTANTS.SETTINGS_NAMES.RECENT_LAB_LIST,
    );

    const [saveOrderDraft, { loading: draftLoading }] = useMutation(saveOrderDraftTemplate, {
        onCompleted: () => {
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_SAVED') });
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const saveDraftDeliveryDetails = async (moveToNextStep: boolean) => {
        await saveOrderDraft({
            variables: { orderId: order._id, order: actionOrderDeliveryDraft(order) },
        });
        if (moveToNextStep) {
            onNextClick();
        }
    };

    // DD-2506: the selection is the restorations this flow will save. The server prices each
    // lab as the save would; with the order's id its stored rows stay frozen and its own
    // action set prices anything new, so the review shows the price the save stores.
    const rankingRestorations = (order: ActionOrderInput) =>
        order.constructions
            .filter((construction: ActionConstruction) => construction.actions.length > 0)
            .map(actionConstructionToRestorationInput);

    const setLabData = (labsWithRankings: LabWithRanking[]) => {
        setLabs(labsWithRankings);
        setOriginalLabs(labsWithRankings);
        const rank1Lab = labsWithRankings.find((lab) => lab.rank === 1);
        const orderLab = labsWithRankings.find((lab) => lab.labId === order.supplier?._id);
        if (rank1Lab) {
            setRankOneLab(rank1Lab);
        }
        const firstLab = labsWithRankings[0];
        const lab = orderLab || firstLab;
        if (lab) {
            setLab(lab);
            setOrder((prev) => ({
                ...prev,
                supplier: {
                    _id: lab.labId,
                    name: lab.name,
                    openingHours: lab.openingHours,
                    holidayDays: lab.holidayDays,
                },
                pricedRestorations: lab.restorations,
                totalPrice: lab.totalPrice,
                totalVat: lab.totalVat,
                currency: lab.currency,
                deliveryDetails: {
                    ...prev.deliveryDetails,
                    ...(order.inputType !== OrderInputType.MODIFY && {
                        date:
                            lab.deliveryDate.deliveryDate && !lab.deliveryDate.isDeliveryDateAHoliday
                                ? new Date(lab.deliveryDate.deliveryDate)
                                : null,
                    }),
                    time: prev.deliveryDetails.time,
                },
            }));
            if (order.inputType !== OrderInputType.MODIFY) {
                setIsHoliday(lab.deliveryDate.isDeliveryDateAHoliday);
            }
        }
    };

    const { loading } = useQuery(getLabsWithRankingsTemplate, {
        fetchPolicy: 'no-cache',
        variables: {
            isModifyOrWarrantyFlow:
                order?.inputType === OrderInputType.MODIFY || order?.inputType === OrderInputType.WARRANTY,
            clinicId: order.customer?._id || currentUser.organization,
            restorations: rankingRestorations(order),
            ...(order._id ? { orderId: order._id } : {}),
            ...((!order.isDraft && order._id && order.supplier) ||
            order.inputType === OrderInputType.WARRANTY ||
            permissions.isLabTechnician(currentUser.type)
                ? { selectedLabId: order.supplier?._id }
                : {}),
        },
        onCompleted({ labsWithRankings }: { labsWithRankings: LabWithRanking[] }) {
            setLabData(labsWithRankings);
        },
        onError(error: Error) {
            console.log(error, 'error');
            showBoundary(error);
        },
    });

    const [getRecentlyUsedLabsWithRankings, { loading: loadingRecentlyUsedLabs }] = useLazyQuery(
        getRecentlyUsedLabsWithRankingsTemplate,
        {
            fetchPolicy: 'no-cache',
            variables: {
                restorations: rankingRestorations(order),
            },
            onCompleted({ recentlyUsedLabsWithRankings }: { recentlyUsedLabsWithRankings: LabWithRanking[] }) {
                setRecentlyUsedLabs(recentlyUsedLabsWithRankings);
                setOriginalRecentlyUsedLabs(recentlyUsedLabsWithRankings);
            },
            onError(error: Error) {
                console.log(error, 'error');
                showBoundary(error);
            },
        },
    );

    useEffect(() => {
        if ((!order?._id || order?.isDraft) && isRecentlyUsedLabsEnabled) {
            getRecentlyUsedLabsWithRankings({
                variables: {
                    restorations: rankingRestorations(order),
                },
            });
        }
    }, [isRecentlyUsedLabsEnabled]);

    useEffect(() => {
        if (order) {
            const validation =
                isValidDeliveryDate(order.deliveryDetails.date) &&
                !isHoliday &&
                !!order.deliveryDetails.destination?._id &&
                !!order.deliveryDetails.pickUpLocation?._id &&
                !!order.sample.type &&
                // The chosen lab's connection must carry a currency; there is no fallback.
                isRealCurrency(order.currency) &&
                (permissions.isLabTechnician(currentUser.type) || order.deliveryDetails.isPickUpAtClinic !== undefined);

            if (order.supplier?._id === rankOneLab.labId) {
                setIsValid(validation);
            } else {
                if ((!order.isDraft && order._id) || order.inputType === OrderInputType.WARRANTY) {
                    setIsValid(validation);
                } else {
                    setIsValid(validation && Object.keys(order.labExceptions || {}).length > 0);
                }
            }
            return;
        }
        setIsValid(false);
    }, [order, isHoliday]);

    const handleDeliveryDateChange = (
        date: Date | null,
        hour: number | undefined,
        minute: number | undefined,
        isDateChange: boolean,
    ) => {
        if (isHoliday && isDateChange) {
            setIsHoliday(false);
        }
        setOrder((prev) => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                date,
                time: {
                    hour: hour,
                    minute: minute,
                },
            },
        }));
    };

    const handleSampleSelection = (sample: Sample) => {
        setOrder((prev) => ({
            ...prev,
            sample,
        }));
    };

    const handleExceptionSelection = (labExceptions: Record<string, boolean>) => {
        setOrder((prev) => ({
            ...prev,
            labExceptions: {
                ...prev.labExceptions,
                ...labExceptions,
            },
        }));
    };

    const handlePickUpLocationChange = (clinic: Organization) => {
        setOrder((prev) => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                pickUpLocation: clinic,
            },
        }));
    };

    const handleDestinationChange = (clinic: Organization) => {
        setOrder((prev) => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                destination: clinic,
            },
        }));
    };

    const handleLabChange = (lab: LabWithRanking) => {
        setLab(lab);
        setIsHoliday(lab.deliveryDate.isDeliveryDateAHoliday);
        setOrder((prev) => ({
            ...prev,
            labExceptions: {},
            supplier: { _id: lab.labId, name: lab.name, openingHours: lab.openingHours, holidayDays: lab.holidayDays },
            deliveryDetails: {
                ...prev.deliveryDetails,
                date: lab.deliveryDate.deliveryDate ? new Date(lab.deliveryDate.deliveryDate) : null,
                time: prev.deliveryDetails.time,
            },
            pricedRestorations: lab.restorations,
            totalPrice: lab.totalPrice,
            totalVat: lab.totalVat,
            currency: lab.currency,
        }));
    };

    const sortLabsBy = (labs: LabWithRanking[], orderBy: OrderBy) => {
        return labs.sort((a, b) => {
            if (orderBy.orderBy === 'price') {
                return orderBy.orderDirection === OrderDirection.asc
                    ? a.totalPrice - b.totalPrice
                    : b.totalPrice - a.totalPrice;
            }
            if (orderBy.orderBy === 'deliveryDate') {
                const dateA = new Date(a.deliveryDate.deliveryDate).getTime();
                const dateB = new Date(b.deliveryDate.deliveryDate).getTime();
                return orderBy.orderDirection === OrderDirection.asc ? dateA - dateB : dateB - dateA;
            }
            return orderBy.orderDirection === OrderDirection.asc ? a.rank - b.rank : b.rank - a.rank;
        });
    };

    const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
        setOrder((prev) => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                isPickUpAtClinic: event.target.value === 'true',
            },
        }));
    };

    if (loading || loadingRecentlyUsedLabs) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Box>
                <Box mb={2}>
                    <Typography variant="h1">{translate('orders:LAB_DELIVERY')}</Typography>
                </Box>
                <Box mb={2}>
                    <Divider />
                </Box>
                {order.inputType !== OrderInputType.WARRANTY &&
                    !permissions.isLabTechnician(currentUser.type) &&
                    (!order._id || order.isDraft) &&
                    isRecentlyUsedLabsEnabled &&
                    recentlyUsedLabs.length > 0 && (
                        <Box mb={4}>
                            <Box mb={2}>
                                <Typography variant="h3" gutterBottom>
                                    {translate('orders:RECENTLY_USED_LABS')}
                                </Typography>
                            </Box>
                            <Box>
                                <LabTable
                                    labs={recentlyUsedLabs}
                                    disableList={!!(!order.isDraft && order._id && order.supplier)}
                                    onLabSelected={(lab: LabWithRanking) => handleLabChange(lab)}
                                    onSort={(orderBy: OrderBy) => {
                                        setRecentlyUsedLabs(sortLabsBy(originalRecentlyUsedLabs, orderBy));
                                    }}
                                    selectedLabId={order.supplier?._id}
                                />
                            </Box>
                        </Box>
                    )}

                {order.inputType !== OrderInputType.WARRANTY &&
                    !permissions.isLabTechnician(currentUser.type) &&
                    order.isDraft && (
                        <Box mb={4}>
                            <Box mb={2}>
                                <Typography variant="h3" gutterBottom>
                                    {translate('orders:CHOOSE_LAB')}
                                </Typography>
                            </Box>
                            <Box>
                                <LabTable
                                    labs={labs}
                                    onSort={(orderBy: OrderBy) => {
                                        setLabs(sortLabsBy(originalLabs, orderBy));
                                    }}
                                    disableList={!!(!order.isDraft && order._id && order.supplier)}
                                    onLabSelected={(lab: LabWithRanking) => handleLabChange(lab)}
                                    selectedLabId={order.supplier?._id}
                                />
                            </Box>
                        </Box>
                    )}
                <Box mb={4}>
                    <Box mb={2} display="flex">
                        <Typography variant="h3" gutterBottom>
                            {translate('orders:DELIVERY_DETAILS')}
                        </Typography>
                        <RequiredAsterisk />
                    </Box>
                    <Box>
                        <Box mb={4}>
                            {order?.supplier ? (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <DeliveryDatePicker
                                            onChange={handleDeliveryDateChange}
                                            date={order.deliveryDetails.date}
                                            hour={order.deliveryDetails.time.hour}
                                            minute={order.deliveryDetails.time.minute}
                                            lab={order?.supplier as Organization}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DeliveryClinicPicker
                                            clinic={order.deliveryDetails.pickUpLocation}
                                            onClinicSelected={handlePickUpLocationChange}
                                            label={translate('orders:PICK_UP_LOCATION')}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DeliveryClinicPicker
                                            clinic={order.deliveryDetails.destination}
                                            onClinicSelected={handleDestinationChange}
                                            label={translate('orders:DELIVERY_LOCATION')}
                                        />
                                    </Grid>
                                </Grid>
                            ) : (
                                <Alert severity="info">{translate('orders:CHOOSE_LAB_TO_SEE_DELIVERY')}</Alert>
                            )}
                            {order?.supplier && permissions.isDentist(currentUser.type) && (
                                <DeliveryTimeWarning
                                    hour={order.deliveryDetails.time.hour}
                                    dataTest="lab-delivery-step.time-warning"
                                />
                            )}
                            {isHoliday && (
                                <Alert severity="error">{translate('orders:LAB_HOLIDAY_SELECT_DATE_MANUALLY')}</Alert>
                            )}
                            {order.supplier?._id && !isRealCurrency(order.currency) && (
                                <Alert severity="error" data-test="lab-delivery-step.currency-alert">
                                    {translate('orders:LAB_CURRENCY_MISSING')}
                                </Alert>
                            )}
                        </Box>
                        {!permissions.isLabTechnician(currentUser.type) && (
                            <Box mb={4}>
                                <FormControl margin="dense" fullWidth required>
                                    <FormLabel
                                        sx={{ color: 'text.primary', '&.Mui-focused': { color: 'text.primary' } }}
                                    >
                                        {translate('orders:IS_PICKUP_AT_CLINIC')}
                                    </FormLabel>
                                    <RadioGroup
                                        value={
                                            order.deliveryDetails.isPickUpAtClinic === undefined
                                                ? ''
                                                : String(order.deliveryDetails.isPickUpAtClinic)
                                        }
                                        onChange={handleRadioChange}
                                        row
                                    >
                                        <FormControlLabel
                                            value="true"
                                            control={<Radio color="secondary" />}
                                            label={translate('common:YES')}
                                        />
                                        <FormControlLabel
                                            value="false"
                                            control={<Radio color="secondary" />}
                                            label={translate('common:NO')}
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        )}
                        {order.inputType !== OrderInputType.WARRANTY &&
                            !permissions.isLabTechnician(currentUser.type) &&
                            order.isDraft &&
                            (!rankOneLab || rankOneLab.labId !== order.supplier?._id) && (
                                <Box mb={4}>
                                    <Box display="flex">
                                        <Typography variant="h3" mb={1}>
                                            {translate('orders:REASON_FOR_EXCEPTION')}
                                        </Typography>
                                        <RequiredAsterisk />
                                    </Box>
                                    <Box>
                                        <Typography variant="body1" mb={1}>
                                            {translate('orders:SELECT_REASON')}
                                        </Typography>
                                    </Box>
                                    <LabExceptions
                                        handleExceptionSelection={handleExceptionSelection}
                                        labExceptions={lab?.labExceptions || []}
                                        savedExceptions={order.labExceptions || {}}
                                    />
                                </Box>
                            )}
                        <Box mb={4}>
                            <Box display="flex">
                                <Typography variant="h3" mb={1}>
                                    {translate('orders:HOW_WAS_THE_SAMPLE_TAKEN')}
                                </Typography>
                                <RequiredAsterisk />
                            </Box>
                            <SampleMethod handleSampleSelection={handleSampleSelection} sample={order.sample} />
                        </Box>
                    </Box>
                </Box>
                <Box display="flex" mt={2}>
                    <Box flexGrow={1} textAlign="left">
                        <Button type="button" onClick={onBackClick}>
                            {translate('common:BACK')}
                        </Button>
                    </Box>
                    <Box>
                        {((order._id && !order.isDraft) || permissions.isLabTechnician(currentUser.type)) && (
                            <Button disabled={!isValid} type="button" onClick={onNextClick} variant="contained">
                                {translate('common:NEXT_STEP')}
                            </Button>
                        )}
                        {order.isDraft && !permissions.isLabTechnician(currentUser.type) && (
                            <>
                                <Button
                                    disabled={!isValid}
                                    type="button"
                                    onClick={async () => await saveDraftDeliveryDetails(false)}
                                    sx={{ mr: 2 }}
                                >
                                    {translate('common:SAVE_DRAFT')}
                                </Button>
                                <LoadingButton
                                    loading={draftLoading}
                                    variant="contained"
                                    disabled={!isValid || draftLoading}
                                    type="button"
                                    onClick={async () => await saveDraftDeliveryDetails(true)}
                                    data-test="construction-step.draft-submit"
                                >
                                    {translate('common:SAVE_AND_NEXT')}
                                </LoadingButton>
                            </>
                        )}
                    </Box>
                </Box>
            </Box>
        </Fragment>
    );
};

export default LabDeliveryStep;
